import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, TypographyProps } from '@mui/material';
import styled from '@emotion/styled';

import routes from 'constants/routes';
import { RootState, store } from 'redux/store';
import { fetchWalletInvoicesList } from 'redux/slices/wallet/walletInvoicesSlice';
import Table from 'components/Common/StyledTable';
import { dateFormat, timeFormat, formatAmount, getFullName } from 'utils/helpers';
import MuiLoader from 'components/Common/Loader';
import theme from 'theme/theme';

interface ExtendedTypography extends TypographyProps {
    value: string;
}

const { success, warning, error } = theme.palette;

const StyledTypography = styled(Typography)<ExtendedTypography>(({ value }) => ({
    color: value === 'PAID' ? success.main : value === 'PENDING' ? warning.main : error.main,
}));

const columns = [
    { id: 'createdDate', label: 'Date', render: ({ value }: any) => dateFormat(value) },
    { id: 'createdTime', label: 'Time', render: ({ data }: any) => timeFormat(data?.['createdDate']) },
    // Role is as per the amount not the invoice so if the sender then it mean need to send money so I will send money if I received the invoice
    // and we are showing the type on based off invoice not the money
    { id: 'type', label: 'Type', render: ({ data }: any) => (data?.myRole === 'SENDER' ? 'RECEIVED' : 'SENT') },
    {
        id: 'fullName',
        label: 'To/From',
        render: ({ data }: any) => {
            return data?.myRole === 'SENDER' ? data?.sentFrom : data?.sentTo;
        },
    },
    {
        id: 'status',
        label: 'Status',
        render: ({ value }: { value: string }) => <StyledTypography value={value}>{value}</StyledTypography>,
    },
    {
        id: 'amount',
        label: 'Total',
        render: ({ value, data }: any) => {
            const myRole = data?.myRole;
            const status = data?.status;
            const balanceIncreased = myRole === 'RECEIVER' && status === 'PAID';
            const balanceDecreased = myRole === 'SENDER' && status === 'PAID';
            const prefix = balanceIncreased ? '+' : balanceDecreased ? '-' : '';
            return prefix + formatAmount(value, 'cents');
        },
    },
];

function WalletInvoices() {
    const navigate = useNavigate();
    const dispatch = useDispatch<typeof store.dispatch>();
    const { walletInvoicesList, loading, totalCount } = useSelector((state: RootState) => state.wallet.walletInvoices);
    const { pageSize, pageNumber } = useSelector((state: RootState) => state?.app?.table);

    useEffect(() => {
        dispatch(fetchWalletInvoicesList());
    }, [dispatch, pageSize, pageNumber]);

    const navigationHandler = (row: any) => {
        navigate(routes.wallet.invoiceDetails.replace(':id', row.requestMoneyId));
    };

    return (
        <div style={{ margin: '1rem' }}>
            <MuiLoader loading={loading} absolute />
            <Table
                data={walletInvoicesList}
                columns={columns}
                successValue="Complete"
                navigationHandler={navigationHandler}
                totalCount={totalCount}
                serverSidePagination
                loading={loading}
            />
        </div>
    );
}

export default WalletInvoices;
